/**
 * Icon — hand-rolled SVG icon set from the Claude Design output.
 *
 * 24px viewport, 1.5 stroke weight, rounded caps/joins — per Elreign brandbook §6.
 * lucide-react is a dep but the design used custom SVG paths; we keep them verbatim
 * to stay pixel-faithful. Lucide alternatives noted in comments where they exist.
 */

import type React from 'react';

export type IconName =
  | 'overview'
  | 'trust'
  | 'doc'
  | 'res'
  | 'people'
  | 'msg'
  | 'audit'
  | 'sett'
  | 'sign'
  | 'arrow-up'
  | 'arrow-down'
  | 'flat'
  | 'bell'
  | 'search'
  | 'check'
  | 'clock'
  | 'shield'
  | 'tax'
  | 'cash'
  | 'key'
  | 'arrow-right'
  | 'plus'
  | 'team'
  | 'brand'
  | 'plug'
  | 'lock'
  | 'master'
  | 'copy'
  | 'upload'
  | 'external'
  | 'graph';

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function Icon({ name, size = 18, stroke = 1.5, color = 'currentColor', className, style }: IconProps) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: color,
    strokeWidth: stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    // `aria-hidden` added 2026-08-22 (E2E a11y sweep). Every icon here is
    // decorative: the component takes no `title`/`aria-label` prop and never
    // renders one, so an <svg> with no accessible name was landing in the
    // accessibility tree as an unnamed node — 2-3 per trustee screen. It was
    // already contributing NOTHING to any accessible name (a nameless <svg>
    // gives its parent button no name either), so hiding it removes noise
    // without removing information. Meaning is always carried by the adjacent
    // text or the control's own aria-label — that convention has to hold for
    // any icon added here.
    'aria-hidden': true,
    className,
    style,
  };

  switch (name) {
    case 'graph':
      return <svg {...common}><circle cx="6" cy="7" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="15" cy="18" r="2.5"/><path d="M8.4 6.8l7.2-.6M7.4 9.2l6.4 7"/></svg>;
    case 'overview':
      return <svg {...common}><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>;
    case 'trust':
      return <svg {...common}><path d="M12 3l8 4v5c0 4.5-3.4 8.4-8 9-4.6-.6-8-4.5-8-9V7l8-4z"/></svg>;
    case 'doc':
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></svg>;
    case 'res':
      return <svg {...common}><path d="M5 4h14v16l-7-3-7 3z"/><path d="M9 9h6M9 13h4"/></svg>;
    case 'people':
      return <svg {...common}><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M16 4a3 3 0 0 1 0 6"/><path d="M21 19c0-2.4-1.7-4.4-4-4.9"/></svg>;
    case 'msg':
      return <svg {...common}><path d="M4 5h16v11H8l-4 4z"/></svg>;
    case 'audit':
      return <svg {...common}><path d="M4 4h12l4 4v12H4z"/><path d="M9 14l2 2 4-4"/></svg>;
    case 'sett':
      return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
    case 'sign':
      return <svg {...common}><path d="M3 17v3h3l11-11-3-3L3 17z"/><path d="M14 6l3 3"/></svg>;
    case 'arrow-up':
      return <svg {...common}><path d="M7 17L17 7"/><path d="M9 7h8v8"/></svg>;
    case 'arrow-down':
      return <svg {...common}><path d="M7 7l10 10"/><path d="M17 9v8H9"/></svg>;
    case 'flat':
      return <svg {...common}><path d="M5 12h14"/></svg>;
    case 'bell':
      return <svg {...common}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/></svg>;
    case 'search':
      return <svg {...common}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>;
    case 'check':
      // Spelled out rather than spread because it overrides strokeWidth —
      // keep `aria-hidden` in step with `common` above.
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className} style={style}><path d="M5 12l5 5L20 7"/></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 3l8 4v6c0 4.5-3.4 8.4-8 9-4.6-.6-8-4.5-8-9V7l8-4z"/><path d="M9 12l2 2 4-4"/></svg>;
    case 'tax':
      return <svg {...common}><path d="M5 4h14v16H5z"/><path d="M9 9l6 6M15 9l-6 6"/></svg>;
    case 'cash':
      return <svg {...common}><rect x="3" y="6" width="18" height="12" rx="1"/><circle cx="12" cy="12" r="2.5"/></svg>;
    case 'key':
      return <svg {...common}><circle cx="8" cy="15" r="4"/><path d="M11 12l9-9"/><path d="M16 7l3 3"/></svg>;
    case 'arrow-right':
      return <svg {...common}><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>;
    case 'plus':
      return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
    case 'team':
      return <svg {...common}><circle cx="12" cy="8" r="3.5"/><path d="M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7"/></svg>;
    case 'brand':
      return <svg {...common}><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6 2 11c0 5 4 9 9 9 1 0 1.5-.7 1.5-1.5S12 17 12 16c0-1 .9-2 2-2h2c3.3 0 6-2.7 6-6 0-3.3-4.5-6-10-6z"/></svg>;
    case 'plug':
      return <svg {...common}><path d="M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-12 0V8zM12 17v5"/></svg>;
    case 'lock':
      return <svg {...common}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>;
    case 'master':
      return <svg {...common}><path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6"/></svg>;
    case 'copy':
      return <svg {...common}><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></svg>;
    case 'upload':
      return <svg {...common}><path d="M12 16V4M6 10l6-6 6 6"/><path d="M4 20h16"/></svg>;
    case 'external':
      return <svg {...common}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
    default:
      return null;
  }
}
